///
/// EarthBrowser
///
/// Satellite Data Provider
///
/// Queries SQLite directly for pagination/search/sort to avoid loading
/// the full satellite dataset into memory
///
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { DataRow, IDataProvider, DataQuery, QueryResult, ColumnMetadata, DataStats, FilterGroup, ProviderCapabilities } from './types'
import { SatelliteTLETable } from '@/earthbrowser/dataset-distribution/satellites/SatelliteTLETable'

export class SatelliteDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	// CelesTrak group ids to show (any match); empty means the whole catalog
	private groupIds: readonly string[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		groupIds?: readonly string[]
	}) {
		this.id = config.id
		this.name = config.name
		this.groupIds = config.groupIds ?? []
	}

	async initialize(): Promise<void> {
		this.isInitializedFlag = true
	}

	get isInitialized(): boolean {
		return this.isInitializedFlag
	}

	setGroupIds(groupIds: readonly string[]): void {
		this.groupIds = groupIds
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: false,
		supportsGeometry: false,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'object_name',
			name: 'Name',
			priority: 1,
			minWidth: 180,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<div className="font-medium truncate">{value as string}</div>
			)
		},
		{
			id: 'norad_cat_id',
			name: 'NORAD ID',
			priority: 2,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const noradId = value as number
				return (
					<span className="text-purple-400/80 font-mono">
						{noradId ? Math.round(noradId) : '—'}
					</span>
				)
			}
		},
		{
			id: 'inclination',
			name: 'Inclination',
			priority: 3,
			minWidth: 110,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const incl = value as number
				return (
					<span className="text-cyan-400/80">
						{incl != null ? `${incl.toFixed(2)}°` : '—'}
					</span>
				)
			}
		},
		{
			id: 'eccentricity',
			name: 'Eccentricity',
			priority: 5,
			minWidth: 110,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const ecc = value as number
				return (
					<span className="text-blue-400/80 font-mono text-sm">
						{ecc != null ? ecc.toFixed(4) : '—'}
					</span>
				)
			}
		},
		{
			id: 'epoch',
			name: 'Data Age',
			priority: 4,
			minWidth: 110,
			type: 'date',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">Unknown</span>
				const epochDate = new Date(value as string)
				const now = new Date()
				const ageMs = now.getTime() - epochDate.getTime()
				const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24))

				let text: string
				let colorClass: string

				if (ageDays === 0) {
					text = 'Today'
					colorClass = 'text-green-400'
				} else if (ageDays === 1) {
					text = '1 day'
					colorClass = 'text-green-400/80'
				} else if (ageDays < 7) {
					text = `${ageDays} days`
					colorClass = 'text-yellow-400/80'
				} else if (ageDays < 30) {
					text = `${ageDays} days`
					colorClass = 'text-orange-400/80'
				} else {
					const ageMonths = Math.floor(ageDays / 30)
					text = `${ageMonths} month${ageMonths > 1 ? 's' : ''}`
					colorClass = 'text-red-400/80'
				}

				return (
					<span
						className={`${colorClass} cursor-help`}
						title={epochDate.toLocaleDateString()}
					>
						{text}
					</span>
				)
			}
		},
		{
			id: 'mean_motion',
			name: 'Revs/Day',
			priority: 5,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const mm = value as number
				return (
					<span className="text-white/80 font-mono text-sm">
						{mm != null ? mm.toFixed(2) : '—'}
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		const count = await this.count()
		return {
			totalRows: count,
			columnCount: this.columnMetadata.length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		const sortSpec = query.sort?.[0]
		const result = await SatelliteTLETable.queryPaginated({
			groupIds: this.groupIds,
			search: query.search?.query,
			sortColumn: sortSpec?.column,
			sortDirection: sortSpec?.direction,
			offset: query.offset ?? 0,
			limit: query.limit ?? 200,
		})

		return {
			rows: result.rows as unknown as DataRow[],
			totalCount: result.totalCount,
			offset: query.offset ?? 0,
			limit: query.limit ?? 200,
			hasMore: (query.offset ?? 0) + (query.limit ?? 200) < result.totalCount,
			executionTime: 0,
			fromCache: false
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		if (this.groupIds.length === 0) {
			return SatelliteTLETable.getCount()
		}
		return SatelliteTLETable.getCountByGroupIds(this.groupIds)
	}

	async dispose(): Promise<void> {
		this.isInitializedFlag = false
	}
}
